import React, { memo, useMemo, useState } from 'react';
import { Intervention } from '../../types';

interface InterventionTableProps {
  interventions: Intervention[];
  isGOF: boolean;
  canModify?: boolean;
  userRole?: 'utilisateur' | 'gof' | 'technicentre';
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onRequestModification?: (id: number) => void;
  onDuplicate?: (intervention: Intervention) => void;
}

// ===== Composant InterventionTable =====
const InterventionTable: React.FC<InterventionTableProps> = ({
  interventions,
  isGOF,
  canModify,
  userRole,
  onEdit,
  onDelete,
  onRequestModification,
  onDuplicate,
}) => {
  const [hoveredEngin, setHoveredEngin] = useState<string | null>(null);

  const formatDateTime = useMemo(() => (dateString: string) => {
    if (!dateString) return <span>-</span>;
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('fr-FR', {
        timeZone: 'Europe/Paris',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const timeStr = date.toLocaleTimeString('fr-FR', {
        timeZone: 'Europe/Paris',
        hour: '2-digit',
        minute: '2-digit',
      });
      return (
        <div className="text-center">
          <div className="text-xs font-medium">{dateStr}</div>
          <div className="text-xs text-gray-600">{timeStr}</div>
        </div>
      );
    } catch {
      return <span>-</span>;
    }
  }, []);

  const formatDateOnly = useMemo(() => (dateString: string) => {
    if (!dateString) return <span>-</span>;
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('fr-FR', {
        timeZone: 'Europe/Paris',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      return (
        <div className="text-center">
          <div className="text-xs font-medium">{dateStr}</div>
        </div>
      );
    } catch {
      return <span>-</span>;
    }
  }, []);

  const formatLieuWithLieuDit = (lieu?: string, lieuDit?: string) => {
    const parts = [];
    if (lieu) parts.push(lieu);
    if (lieuDit) parts.push(lieuDit);
    return parts.length > 0 ? parts : ['-'];
  };

  const getStatusForEngin = (intervention: Intervention, enginId: string, statusType: string) => {
    if (statusType === 'validation') {

      try {
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
        const status = validationData[enginId];

        if (status && ['done', 'not_feasible', 'pending'].includes(status)) {
          return status;
        }
      } catch {

      }

      if (intervention.statut_validation === 'non') {
        return 'not_feasible';
      }

      if (intervention.statut_validation === 'oui') {
        return 'done';
      }

      return 'pending';
    }

    const statusFieldMap: Record<string, keyof Intervention> = {
      'veille_gps': 'veille_gps_donnees',
      'rdv_fait': 'rdv_fait_donnees',
      'realise': 'realise_donnees'
    };

    const statusField = statusFieldMap[statusType];

    try {
      const statusData = intervention[statusField] as string;
      if (!statusData) return null;
      const parsed = JSON.parse(statusData);
      return parsed[enginId];
    } catch {
      return intervention[statusType as keyof Intervention] === 'oui' ? 'done' : null;
    }
  };

  const getCommentForEngin = (intervention: Intervention, enginId: string, statusType: string) => {
    if (statusType === 'validation') {
      if (intervention.statut_validation === 'non') {
        return intervention.motif_refus || intervention.commentaires || 'Refusé par le SR';
      }

      try {
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
        const status = validationData[enginId];

        if (status === 'done') {
          return 'Engin validé';
        } else if (status === 'not_feasible') {
          try {
            const validationComments = intervention.commentaires_validation_donnees ? JSON.parse(intervention.commentaires_validation_donnees) : {};
            const comment = validationComments[enginId];
            return comment || intervention.commentaires || 'Engin refusé';
          } catch {
            return intervention.commentaires || 'Engin refusé';
          }
        } else if (status === 'pending') {
          return intervention.commentaires || 'En attente de validation';
        }
      } catch {
      }

      if (intervention.statut_validation === 'oui') {
        return 'Engin validé';
      }

      return intervention.commentaires || 'En attente de validation';
    }

    const commentFieldMap: Record<string, keyof Intervention> = {
      'veille_gps': 'commentaires_gps_donnees',
      'rdv_fait': 'commentaires_rdv_donnees',
      'realise': 'commentaires_realise_donnees'
    };

    const commentField = commentFieldMap[statusType];

    try {
      const commentData = intervention[commentField] as string;
      if (!commentData) return intervention.commentaires || '';
      const parsed = JSON.parse(commentData);
      return parsed[enginId] || intervention.commentaires || '';
    } catch {
      return intervention.commentaires || '';
    }
  };

  const isAllEnginesRealized = (intervention: Intervention): boolean => {
    const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
    if (engins.length === 0) return false;

    try {
      const realiseData = intervention.realise_donnees ? JSON.parse(intervention.realise_donnees) : {};
      const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
      const enginIds = engins.map((_, index) => `engin_${index + 1}`);

      const nonRefusedEnginIds = enginIds.filter(enginId =>
        validationData[enginId] !== 'not_feasible'
      );

      if (nonRefusedEnginIds.length === 0) return false;

      return nonRefusedEnginIds.every(enginId => realiseData[enginId] === 'done');
    } catch {
      return false;
    }
  };

  const getRowClass = (intervention: Intervention) => {
    if (intervention.annulation_client) {
      return 'row-orange';
    }

    const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];

    if (engins.length > 0) {
      try {
        const realiseData = intervention.realise_donnees ? JSON.parse(intervention.realise_donnees) : {};
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};

        const enginIds = engins.map((_, index) => `engin_${index + 1}`);

        const nonRefusedEnginIds = enginIds.filter(enginId =>
          validationData[enginId] !== 'not_feasible'
        );

        if (nonRefusedEnginIds.length > 0) {
          const allRealized = nonRefusedEnginIds.every(enginId =>
            realiseData[enginId] === 'done'
          );
          if (allRealized) {
            return 'row-green';
          }
        }

        const allEnginsNotFeasibleRealise = enginIds.every(enginId =>
          realiseData[enginId] === 'not_feasible'
        );
        if (allEnginsNotFeasibleRealise && enginIds.length > 0) {
          return 'row-red';
        }

        const allEnginsRefused = enginIds.every(enginId =>
          validationData[enginId] === 'not_feasible'
        );
        if (allEnginsRefused && enginIds.length > 0) {
          return 'row-yellow';
        }

        if (nonRefusedEnginIds.length > 0) {

          if (intervention.statut_validation === 'oui') {
            return 'row-purple';
          }

          const allValidated = nonRefusedEnginIds.every(enginId =>
            validationData[enginId] === 'done'
          );
          if (allValidated) {
            return 'row-purple';
          }

          const allPending = nonRefusedEnginIds.every(enginId =>
            !validationData[enginId] || validationData[enginId] === 'pending'
          );
          if (allPending) {
            return 'row-blue';
          }
        }
      } catch (error) {
        console.error('Error parsing engine data:', error);
      }
    }

    return 'bg-white';
  };

  const renderEnginStatus = (intervention: Intervention, type: string) => {
    const allEngines = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4];

    if (allEngines.filter(Boolean).length === 0) return '-';

    let enginesToShow = allEngines;

    if (type !== 'validation') {
      try {
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};

        enginesToShow = allEngines.filter((engin, index) => {
          if (!engin) return false;
          const enginId = `engin_${index + 1}`;
          return validationData[enginId] !== 'not_feasible';
        });
      } catch {
        enginesToShow = allEngines;
      }
    }

    if (enginesToShow.filter(Boolean).length === 0) return '-';

    return (
      <div className="flex flex-wrap gap-1 justify-center">
        {enginesToShow.map((engin, displayIndex) => {
          if (!engin) return null;

          let originalIndex = -1;
          let count = 0;
          for (let i = 0; i < allEngines.length; i++) {
            if (type !== 'validation') {

              try {
                const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
                const tempEnginId = `engin_${i + 1}`;
                if (allEngines[i] && validationData[tempEnginId] === 'not_feasible') {
                  continue;
                }
              } catch {
              }
            }
            if (allEngines[i]) {
              if (count === displayIndex) {
                originalIndex = i;
                break;
              }
              count++;
            }
          }

          if (originalIndex === -1) return null;

          const enginId = `engin_${originalIndex + 1}`;
          const status = getStatusForEngin(intervention, enginId, type);
          const comment = getCommentForEngin(intervention, enginId, type);
          let badgeClass = 'badge-warning';

          if (status === 'done') badgeClass = 'badge-success';
          else if (status === 'not_feasible') badgeClass = 'badge-danger';
          else badgeClass = 'badge-warning';

          const tooltipText = comment ? `${engin}: ${comment}` : `${engin}: En attente`;

          return (
            <span
              key={`${intervention.id}-${originalIndex}-${enginId}`}
              className={`badge ${badgeClass} text-xs cursor-help shadow-md hover:shadow-lg transition-shadow duration-200`}
              onMouseEnter={() => setHoveredEngin(`${intervention.id}-${enginId}`)}
              onMouseLeave={() => setHoveredEngin(null)}
              title={tooltipText}
            >
              {engin}
            </span>
          );
        })}
      </div>
    );
  };

  if (interventions.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-clipboard-list text-gray-300 text-6xl mb-4"></i>
        <h3 className="text-xl font-medium text-gray-600 mb-2">Aucune intervention</h3>
        <p className="text-gray-500">Commencez par ajouter votre première intervention</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th className="text-center">Sem.</th>
            <th className="text-center">Prestation</th>
            <th className="text-center">Contact</th>
            <th className="text-center">Campagne</th>
            <th className="text-center">N° ELAN</th>
            <th className="text-center">Lieu</th>
            <th className="text-center">Dispo</th>
            <th className="text-center">Départ</th>
            <th className="text-center">Validation SR</th>
            <th className="text-center">Date<br />intervention</th>
            <th className="text-center">TC</th>
            <th className="text-center">GPS</th>
            <th className="text-center">RDV</th>
            <th className="text-center">Réalisé</th>
            {(isGOF || canModify || userRole === 'technicentre') && <th className="text-center">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {interventions.map((intervention) => {
            const rowTooltip = intervention.commentaires
              ? `Commentaire général: ${intervention.commentaires}`
              : '';

            return (
              <tr
                key={intervention.id}
                className={getRowClass(intervention)}
                onMouseLeave={() => setHoveredEngin(null)}
                title={!hoveredEngin ? rowTooltip : ''}
              >
                <td className="text-center">
                  <span className="badge badge-primary">
                    S{intervention.numero_semaine || '-'}
                  </span>
                </td>
                <td className="text-center">
                  <span className="badge badge-info">
                    {intervention.prestation_nom || '-'}
                  </span>
                </td>
                <td className="text-wrap text-center">{intervention.contact}</td>
                <td className="text-center">{intervention.campagne_nom || '-'}</td>
                <td className="font-mono text-sm text-center">{intervention.numero_elan || '-'}</td>
                <td className="text-wrap text-center">
                  {formatLieuWithLieuDit(intervention.site_nom, intervention.lieu_dit).map((line, index) => (
                    <div key={index} className={index === 0 ? 'font-medium' : 'text-xs text-gray-600 mt-1'}>
                      {line}
                    </div>
                  ))}
                </td>
                <td className="text-center">
                  {formatDateTime(intervention.disponibilite_a_partir)}
                </td>
                <td className="text-center">
                  {formatDateTime(intervention.depart_pour_mission)}
                </td>
                <td className="text-center">
                  {renderEnginStatus(intervention, 'validation')}
                </td>
                <td className="text-center">
                  {formatDateOnly(intervention.date_intervention || '')}
                </td>
                <td className="text-center">{intervention.technicentre_nom || '-'}</td>
                <td className="text-center">
                  {renderEnginStatus(intervention, 'veille_gps')}
                </td>
                <td className="text-center">
                  {renderEnginStatus(intervention, 'rdv_fait')}
                </td>
                <td className="text-center">
                  {renderEnginStatus(intervention, 'realise')}
                </td>
                {(isGOF || canModify || userRole === 'technicentre') && (
                  <td>
                    <div className="flex justify-center space-x-2">
                      {isGOF ? (
                        <>
                          <button
                            onClick={() => onEdit(intervention.id)}
                            className="px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                            title="Modifier"
                          >
                            <i className="fas fa-edit text-lg"></i>
                          </button>
                          <button
                            onClick={() => onDuplicate?.(intervention)}
                            className="px-3 py-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                            title="Dupliquer (pré-remplir nouvelle intervention)"
                          >
                            <i className="fas fa-copy text-lg"></i>
                          </button>
                          <button
                            onClick={() => onDelete(intervention.id)}
                            className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                            title="Supprimer"
                          >
                            <i className="fas fa-trash text-lg"></i>
                          </button>
                        </>
                      ) : userRole === 'technicentre' ? (
                        <button
                          onClick={() => onEdit(intervention.id)}
                          className="px-3 py-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                          title="Valider l'intervention"
                        >
                          <i className="fas fa-check-circle text-lg"></i>
                        </button>
                      ) : canModify ? (
                        <>
                          {intervention.annulation_client ? (
                            <div className="flex items-center space-x-2 px-3 py-2 text-orange-400">
                              <i className="fas fa-ban text-sm"></i>
                              <span className="text-xs">Annulation client</span>
                            </div>
                          ) : isAllEnginesRealized(intervention) ? (
                            <div className="flex items-center space-x-2 px-3 py-2 text-gray-400">
                              <i className="fas fa-lock text-sm"></i>
                              <span className="text-xs">Intervention terminée</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => onRequestModification?.(intervention.id)}
                              className="px-3 py-2 text-orange-600 hover:text-orange-800 hover:bg-orange-100 rounded transition-colors"
                              title="Demander une modification"
                            >
                              <i className="fas fa-edit text-lg"></i>
                            </button>
                          )}
                          <button
                            onClick={() => onDuplicate?.(intervention)}
                            className="px-3 py-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                            title="Dupliquer (pré-remplir nouvelle intervention)"
                          >
                            <i className="fas fa-copy text-lg"></i>
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default memo(InterventionTable);
